import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { GAME_ASSET_SLUG } from "@/config/game";
import { Button } from "@/design-system/Button/Button";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { cn } from "@/lib/utils";
import {
  CurrentStepData,
  EvolutionBuilderPlayer,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
  tierSelector,
} from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import type { GroupedUpgrades } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { X } from "lucide-react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { CardBuilderVariant } from "../CardBuilderTopSection";
import { UpgradeChip } from "./UpgradeChips";

interface PlayerAndUpgradeSectionProps {
  basePlayer: EvolutionBuilderPlayer;
  currentEvolutionPlayer: EvolutionBuilderPlayer;
  isEvolutionPlayer: boolean;
  handlePlayerClicked: () => void;
  loading?: boolean;
  condensed?: boolean;
  currentStepData: CurrentStepData | null;
  upgrades: GroupedUpgrades;
  handleClearPlaystyles?: () => void;
  variant?: CardBuilderVariant;
  onRemovePlayStyle?: (id: number, plus: boolean) => void;
  removeDisabled?: boolean;
}

const cdnBase = import.meta.env.VITE_APP_ASSETS_BASE_URL;

/** The player card's scale in the in-flow header; condensed is smaller. */
export const HEADER_CARD_SCALE = 0.8;

/**
 * The builder's header: the player as they stand after the chain, their meta
 * rating and role, and everything the chain has granted so far. Condensed
 * once the page scrolls past it.
 */
export const PlayerAndUpgradeSection = ({
  basePlayer,
  currentEvolutionPlayer,
  isEvolutionPlayer,
  handlePlayerClicked,
  condensed = false,
  upgrades,
  handleClearPlaystyles,
  variant = "evo-builder",
  onRemovePlayStyle,
  removeDisabled,
}: PlayerAndUpgradeSectionProps) => {
  const { t } = useTranslation("evoBuilder");
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isPlayStylesLab = variant === "playstyles-lab";
  const playStylesUpgrades = [
    ...upgrades.playStylesPlus,
    ...upgrades.playStyles,
  ];
  const metaRatingUpgrade = upgrades.metaRating;
  const ovrUpgrade = upgrades.ovr;
  const attributeUpgrades = upgrades.attributes;
  const positionUpgrades = upgrades.positions;
  const skillMovesUpgrade = upgrades.skillMoves.at(-1);
  const weakFootUpgrade = upgrades.weakFoot.at(-1);
  const { positions, playerAttributes } = useSelector(contentLabelsSelector);

  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({
      playerRoleId: currentEvolutionPlayer?.playerRoleId,
      shortName: isTouchDevice,
    }),
  );
  const basePlayerTier = useSelector(
    tierSelector(basePlayer?.metaRating, basePlayer?.playerRoleId, true),
  );
  const currentPlayerTier = useSelector(
    tierSelector(
      currentEvolutionPlayer?.metaRating,
      currentEvolutionPlayer?.playerRoleId,
      true,
    ),
  );
  const isRolesPlusOrPlusPlus = currentEvolutionPlayer
    ? getRolesPlusOrPlusPlus({
        playerRoleId: currentEvolutionPlayer.playerRoleId,
        rolesPlus: currentEvolutionPlayer.rolesPlus,
      })
    : undefined;

  // A PlayStyle upgraded to its plus tier moves out of the base row.
  const upgradedToPlusIds = useMemo(
    () =>
      playStylesUpgrades?.filter((u) => u.isPlus).map((u) => u.playStyleId) ??
      [],
    [playStylesUpgrades],
  );
  const basePlayStyles = useMemo(
    () =>
      basePlayer?.playStyles?.filter((ps) => !upgradedToPlusIds.includes(ps)) ??
      [],
    [basePlayer?.playStyles, upgradedToPlusIds],
  );
  const basePlayStylesPlus = basePlayer?.playStylesPlus ?? [];
  const hasBasePlayStyles =
    basePlayStylesPlus.length > 0 || basePlayStyles.length > 0;
  const hasPlayStyleUpgrades = playStylesUpgrades?.length > 0;
  const hasMetaGain = metaRatingUpgrade?.metaRating?.delta > 0;

  if (!currentEvolutionPlayer) return null;

  const iconSize = condensed ? "size-6" : "size-7";
  const cardScale = condensed ? (isTouchDevice ? 0.6 : 0.5) : HEADER_CARD_SCALE;

  return (
    <div className="flex w-full items-center gap-3 touch-device:gap-5">
      <button
        type="button"
        onClick={handlePlayerClicked}
        className="shrink-0 cursor-pointer transition-transform duration-200 ease-out hover:scale-[1.02]"
      >
        <PlayerCard
          player={currentEvolutionPlayer}
          position={currentEvolutionPlayer.preferredPosition}
          scale={cardScale}
          isEvolution={
            isEvolutionPlayer ||
            currentEvolutionPlayer.evolutionPath?.length > 0 ||
            currentEvolutionPlayer.evoPathId !== undefined
          }
          hidePrice
        />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {currentEvolutionPlayer.metaRating && (
            <MetaRatingChip
              metaRating={currentEvolutionPlayer.metaRating}
              tier={currentPlayerTier}
              basePlayerMetaRating={
                hasMetaGain ? basePlayer?.metaRating : undefined
              }
              basePlayerTier={hasMetaGain ? basePlayerTier : undefined}
            />
          )}
          {playerRoleLabel && (
            <span className="inline-flex h-7 min-w-0 items-center gap-1.5 rounded-full border border-edge-strong bg-surface-raised px-2.5 text-xs text-foreground-body">
              <span className="truncate">{playerRoleLabel}</span>
              <PlusPlus
                plus={isRolesPlusOrPlusPlus === "plus"}
                plusPlus={isRolesPlusOrPlusPlus === "plusPlus"}
              />
            </span>
          )}
        </div>

        <div
          className={cn(
            "flex flex-col gap-2 rounded-xl bg-background/70 p-2.5",
            condensed && "max-h-24 overflow-hidden",
          )}
        >
          {/* PlayStyles — the row only exists when there is one to show. */}
          {(hasBasePlayStyles || hasPlayStyleUpgrades || isPlayStylesLab) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {isPlayStylesLab &&
                !hasBasePlayStyles &&
                !hasPlayStyleUpgrades && (
                  <span className="text-xs text-foreground-muted">
                    {t("playStylesLab.addPlayStyles")}
                  </span>
                )}
              {basePlayStylesPlus.map((ps) => (
                <img
                  key={`base-ps-plus-${ps}`}
                  className={iconSize}
                  src={`${cdnBase}/${GAME_ASSET_SLUG}/playstylesplus/${ps}.png`}
                  alt=""
                />
              ))}
              {basePlayStyles.map((ps) => (
                <img
                  key={`base-ps-${ps}`}
                  className={iconSize}
                  src={`${cdnBase}/${GAME_ASSET_SLUG}/playstyles/${ps}.png`}
                  alt=""
                />
              ))}
              {hasBasePlayStyles && hasPlayStyleUpgrades && <PlusSign />}
              {hasPlayStyleUpgrades && (
                <div className="flex flex-wrap items-center gap-1 rounded-lg border border-upgrade/40 bg-upgrade/10 p-0.5">
                  {playStylesUpgrades.map((upgrade) => {
                    const src = `${cdnBase}/${GAME_ASSET_SLUG}/${upgrade.isPlus ? "playstylesplus" : "playstyles"}/${upgrade.playStyleId}.png`;
                    const key = `upgrade-ps-${upgrade.isPlus ? "plus-" : ""}${upgrade.playStyleId}`;
                    if (!isPlayStylesLab) {
                      return (
                        <img key={key} className={iconSize} src={src} alt="" />
                      );
                    }
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={removeDisabled}
                        onClick={() =>
                          onRemovePlayStyle?.(
                            upgrade.playStyleId,
                            upgrade.isPlus,
                          )
                        }
                        className="group relative cursor-pointer rounded-md disabled:pointer-events-none"
                      >
                        <img
                          className={cn(
                            iconSize,
                            "transition-opacity group-hover:opacity-30",
                          )}
                          src={src}
                          alt=""
                        />
                        <span className="pointer-events-none absolute inset-0 hidden items-center justify-center text-foreground group-hover:flex">
                          <X className="size-3.5" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {handleClearPlaystyles && hasPlayStyleUpgrades && (
                <Button
                  variant="secondary"
                  size="small"
                  disabled={removeDisabled}
                  onClick={handleClearPlaystyles}
                  className="ml-2"
                >
                  {t("playStylesLab.clearPlayStyles")}
                </Button>
              )}
            </div>
          )}

          {/* Positions and stats */}
          {!isPlayStylesLab && (
            <div className="flex flex-wrap items-center gap-1.5">
              {currentEvolutionPlayer.positions?.map((position) => (
                <UpgradeChip
                  key={`base-pos-${position}`}
                  label={positions[position]?.name ?? position}
                />
              ))}
              {positionUpgrades.length > 0 && <PlusSign />}
              {positionUpgrades.map((upgrade) => (
                <UpgradeChip
                  key={`pos-upgrade-${upgrade.positionId}`}
                  label={upgrade.positionId}
                  accent
                />
              ))}
              {ovrUpgrade && (
                <UpgradeChip label={t("chips.ovr")} value={ovrUpgrade.value} />
              )}
              {weakFootUpgrade && (
                <UpgradeChip
                  label={t("chips.wf")}
                  value={weakFootUpgrade.weakFoot}
                />
              )}
              {skillMovesUpgrade && (
                <UpgradeChip
                  label={t("chips.sm")}
                  value={skillMovesUpgrade.skillMoves}
                />
              )}
              {attributeUpgrades.map((upgrade) => (
                <UpgradeChip
                  key={`attr-${upgrade.id}`}
                  label={
                    playerAttributes[upgrade.attributeKey]?.short ??
                    upgrade.label
                  }
                  value={upgrade.value}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlusSign = () => (
  <span className="text-xs font-semibold text-foreground-muted">+</span>
);
